import { getObjectSchema } from '../Hasp/properties/v2/wireFormat.js'
import { canonicalConnectionData } from '../draw/connectionData.js'
import { FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES, firmwareRecordByteLength } from './firmwareLineLimit.js'

export function attachDesignerEndpointIds(page, pageLines) {
    const endpointNodes = page.find(node => node.obj === 'badge' || node.obj === 'connection')
    const byHaspId = new Map(endpointNodes.map(node => [Number(node.haspid), node]))
    for (const item of pageLines) {
        const node = byHaspId.get(Number(item.id))
        if (node && item.obj === node.obj) item._uuid = node.id()
    }
}

const CONNECTION_AUTHORING_PROPERTIES = [
    'value_source', 'animation_enabled', 'bullet_color', 'bullet_size', 'bullet_count',
]

const BADGE_AUTHORING_PROPERTIES = [
    'text', 'value', 'value_str',
    'text_font_resource', 'text_ofs_x', 'text_ofs_y',
    'value_font', 'value_font_resource', 'value_ofs_x', 'value_ofs_y',
]

/** Persist full-fidelity authoring fields that sparse firmware export may omit as defaults. */
export function attachDesignerAuthoringData(page, pageLines) {
    const nodes = page.find(node => node.obj === 'badge' || node.obj === 'connection')
    const byHaspId = new Map(nodes.map(node => [Number(node.haspid), node]))
    for (const item of pageLines) {
        const node = byHaspId.get(Number(item.id))
        if (!node || item.obj !== node.obj) continue
        if (item.obj === 'connection') {
            Object.assign(item, canonicalConnectionData(node))
            for (const property of CONNECTION_AUTHORING_PROPERTIES) item[property] = node[property]
            continue
        }
        for (const property of BADGE_AUTHORING_PROPERTIES) item[property] = node[property]
        delete item.icon
        delete item.val
        item.ring_source = node.ring_source
        item.ring_reference = node.ring_reference
        item.ring_segments = (Array.isArray(node.ring_segments) ? node.ring_segments : [])
            .map(segment => ({ ...segment }))
    }
}

export function attachDesignerPageRoutingSettings(pageLines, connectionSpacing) {
    const pageRecord = pageLines.find(item => item.page !== undefined && item.id === undefined)
    if (pageRecord) pageRecord.connection_spacing = connectionSpacing
}

export function stripUnsupportedObjects(pageLines) {
    const warnings = []
    for (let index = pageLines.length - 1; index >= 0; index--) {
        const schema = getObjectSchema(pageLines[index].obj)
        if (schema?.firmwareSupported === false) {
            warnings.push(`${pageLines[index].obj} is Designer-only and was omitted from firmware export.`)
            pageLines.splice(index, 1)
        }
    }
    return warnings
}

/**
 * Stable firmware-only partition that gives the plate the same stacking the
 * Designer canvas shows: page header, then connections, then everything else.
 *
 * The firmware has no z-order instruction in its wire format — LVGL siblings
 * stack in creation order, so whatever is emitted last is drawn on top. This
 * function used to emit connections LAST so a connection's `from`/`to` badge
 * ids would already exist, but the firmware resolves no endpoints at all: a
 * connection is drawn purely from its own resolved `points`, so that ordering
 * bought nothing and cost the occlusion. Emitting connections FIRST lets each
 * badge cover the line ends that run underneath it, exactly as
 * placeConnectionBehindPageObjects() does on the canvas — no endpoint is
 * shortened and no routing changes.
 *
 * Only connections are moved. Records within each bucket keep their relative
 * order, so the Designer's own stacking between badges, and between
 * connections, survives untouched. The page header (a record carrying neither
 * `obj` nor `id`, e.g. `{ page: 1 }` or a comment) has to stay first whatever
 * happens: the firmware reads it to decide which page the following lines
 * belong to.
 */
export function orderFirmwarePageRecords(pageLines) {
    const header = []
    const connections = []
    const rest = []
    for (const record of pageLines) {
        if (record?.obj === undefined && record?.id === undefined) header.push(record)
        else if (record?.obj === 'connection') connections.push(record)
        else rest.push(record)
    }

    // The firmware skips an object whose `parentid` does not exist yet, so a
    // connection placed on a container (e.g. a full-page `obj` holding the
    // badges) must be created right AFTER that container: still below the
    // container's later children (the badges), but no longer orphaned.
    const nested = new Map()
    const leading = []
    for (const record of connections) {
        const parentIndex = record.parentid === undefined
            ? -1
            : rest.findIndex(candidate => candidate?.obj !== 'connection' && Number(candidate?.id) === Number(record.parentid))
        if (parentIndex < 0) leading.push(record)
        else nested.set(parentIndex, [...(nested.get(parentIndex) || []), record])
    }

    const ordered = []
    rest.forEach((record, index) => {
        ordered.push(record)
        if (nested.has(index)) ordered.push(...nested.get(index))
    })
    return [...header, ...leading, ...ordered]
}

/** Transport-only split: create each connection first, then update its points. */
export function splitFirmwareConnectionRecords(pageLines) {
    return pageLines.flatMap(record => {
        if (record?.obj !== 'connection' || !Array.isArray(record.points)) return [record]
        const { points, ...createRecord } = record
        return [createRecord, { id: record.id, points }]
    })
}

const BADGE_CREATE_KEYS = ['id', 'obj', 'x', 'y', 'w', 'h', 'parentid', 'radius']
const BADGE_CONTENT_KEYS = [
    'text', 'val', 'value_str',
    'text_font', 'text_size', 'text_ofs_x', 'text_ofs_y',
    'value_font', 'value_size', 'value_ofs_x', 'value_ofs_y',
]
const BADGE_RING_KEYS = ['ring_width', 'ring_start_angle', 'ring_source', 'ring_reference']

function takeProperties(source, keys) {
    const result = {}
    for (const key of keys) {
        if (Object.hasOwn(source, key)) result[key] = source[key]
    }
    return result
}

function splitPropertyGroup(id, properties) {
    const entries = Object.entries(properties)
    if (entries.length === 0) return []
    const whole = { id, ...properties }
    if (firmwareRecordByteLength(whole) <= FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES) return [whole]

    const records = []
    let update = { id }
    for (const [key, value] of entries) {
        const candidate = { ...update, [key]: value }
        if (Object.keys(update).length > 1
            && firmwareRecordByteLength(candidate) > FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES) {
            records.push(update)
            update = { id, [key]: value }
        } else {
            update = candidate
        }
    }
    records.push(update)
    return records
}

/**
 * Transport-only badge split. Ring segments are always a normal object update;
 * other logical groups move out of create only when the create line is too long.
 */
export function splitFirmwareBadgeRecords(records) {
    return records.flatMap(record => {
        if (record?.obj !== 'badge') return [record]

        const ringSegments = record.ring_segments
        const withoutSegments = { ...record }
        delete withoutSegments.ring_segments
        const segmentRecords = Array.isArray(ringSegments)
            ? [{ id: record.id, ring_segments: ringSegments }]
            : []
        if (firmwareRecordByteLength(withoutSegments) <= FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES) {
            return [withoutSegments, ...segmentRecords]
        }

        const create = takeProperties(withoutSegments, BADGE_CREATE_KEYS)
        const content = takeProperties(withoutSegments, BADGE_CONTENT_KEYS)
        const ring = takeProperties(withoutSegments, BADGE_RING_KEYS)
        const groupedKeys = new Set([...BADGE_CREATE_KEYS, ...BADGE_CONTENT_KEYS, ...BADGE_RING_KEYS])
        const style = Object.fromEntries(Object.entries(withoutSegments)
            .filter(([key]) => !groupedKeys.has(key)))

        return [
            create,
            ...splitPropertyGroup(record.id, style),
            ...splitPropertyGroup(record.id, content),
            ...splitPropertyGroup(record.id, ring),
            ...segmentRecords,
        ]
    })
}

function nodesOfType(page, obj) {
    return page.find(node => node.obj === obj)
}

function wireRingSegments(segments) {
    return (Array.isArray(segments) ? segments : []).map(segment => {
        const wire = {
            id: segment.id,
            value: segment.value,
            color: segment.color,
        }
        // Enabled is default-true throughout the badge ring model. Keep only
        // the meaningful false override so four normal segments fit one line.
        if (segment.enabled === false) wire.enabled = false
        return wire
    })
}

function finiteNumber(value, fallback = 0) {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

/** Resolve the rich Designer badge/connection model to the provisional firmware wire model. */
export function resolveBadgeConnectionWireModel(page, pageLines) {
    const badges = nodesOfType(page, 'badge')
    const connections = nodesOfType(page, 'connection')
    const badgeByUuid = new Map(badges.map(node => [String(node.id()), node]))
    const nodeByHaspId = new Map([...badges, ...connections].map(node => [Number(node.haspid), node]))
    const warnings = []

    for (let index = pageLines.length - 1; index >= 0; index--) {
        const item = pageLines[index]
        const node = nodeByHaspId.get(Number(item.id))
        if (!node || item.obj !== node.obj) continue

        if (item.obj === 'badge') {
            item.text = node.text
            item.val = Number.isFinite(Number(node.value)) ? Number(node.value) : 0
            item.value_str = node.value_str
            item.text_font = node.styleProp?.('main', 'text_font')
            item.text_font_resource = node.text_font_resource
            item.text_ofs_x = node.text_ofs_x
            item.text_ofs_y = node.text_ofs_y
            item.value_font = node.value_font
            item.value_font_resource = node.value_font_resource
            item.value_ofs_x = node.value_ofs_x
            item.value_ofs_y = node.value_ofs_y
            item.ring_source = node.ring_source
            item.ring_reference = node.ring_reference
            item.ring_segments = wireRingSegments(node.ring_segments)
            delete item.value
            delete item.icon
            continue
        }

        const source = badgeByUuid.get(String(node.from))
        const target = badgeByUuid.get(String(node.to))
        if (!source || !target || !Array.isArray(node.routePoints) || node.routePoints.length < 2) {
            warnings.push(`Connection ${node.haspid} has unresolved badge endpoints or route and was omitted from firmware export.`)
            pageLines.splice(index, 1)
            continue
        }
        item.from = Number(source.haspid)
        item.to = Number(target.haspid)
        item.line_color = node.line_color
        item.line_width = node.line_width
        item.corner_radius = node.corner_radius
        const data = canonicalConnectionData(node)
        item.min = data.min
        item.max = data.max
        item.val = data.value_source === 'from'
            ? finiteNumber(source.value)
            : data.value_source === 'to'
                ? finiteNumber(target.value)
                : data.value
        item.animation_enabled = node.animation_enabled
        item.bullet_color = node.bullet_color
        item.bullet_size = node.bullet_size
        item.bullet_count = node.bullet_count
        item.points = node.routePoints.map(point => ({ x: Number(point.x), y: Number(point.y) }))
        delete item.from_side
        delete item.to_side
        delete item.value_source
        delete item.speed_factor_q16
        delete item.motion_primitives
        delete item.value_str
        delete item.value
        delete item.direction
        delete item.bullet_spacing
        delete item.min_value
        delete item.max_value
        delete item.min_speed
        delete item.max_speed
    }
    return warnings
}
